import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import s from './Layout.module.scss';
import expandBlue from '../../assets/expand-blue.svg';
import notification from '../../assets/notification.svg';
import { TEST_ENV, STAGING_ENV, PRODUCTION_ENV, ELEIOT_ENV, REFRESH_NOTIFICATION } from '../../constants/index';
import { FormattedMessage, injectIntl, IntlProvider } from 'react-intl';
import Sidebar from '../Sidebar/SidebarContainer';
import classNames from 'classnames';
import { WidgetVisibility } from '../../utils/widgetManager';
import NoticeBoard from '../Notifications/NoticeBoard/NoticeBoard';
import { getNotifications } from '../../selectors/index';
import NotificationAlert from '../Notifications/NotificationAlert/NotificationAlert';
import Cookies from 'js-cookie';
import PinIcon from '../DynamicNavigation/PinIcon';
import PinPageModal from '../DynamicNavigation/PinPageModal';
import useDynamicLinks from '../../hooks/useDynamicLinks';

const Layout = ({
  isAuthenticated = false,
  children,
  logout,
  currentSidebarOpen,
  handleViewSidebar,
  doNotificationPolling,
  resetNotificationAlert,
  location,
  userProfileWidgets,
  intl
}) => {
  const notifications = useSelector(state => getNotifications(state));

  const sidebarClass = currentSidebarOpen ? '' : s.active;
  const contentWrapperClass = currentSidebarOpen ? '' : s.sidebarCollapsed;
  const [show, setShow] = useState(false);
  const [time, setTime] = React.useState(0);
  const [interval, setInterval] = React.useState(100);

  let statusFetchInterval = REFRESH_NOTIFICATION;

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const envName = process.env.REACT_APP_ENV_NAME;
  let showAlert = false;
  let alertMessage = '';
  let alertType = 'info';

  let envClass = s.bgColorDev;
  if (envName === TEST_ENV) {
    envClass = s.bgColorTest;
  } else if (envName === STAGING_ENV) {
    envClass = s.bgColorStage;
  }

  if (notifications.showNotificationAlerts.length > 0) {
    if (notifications.showNotificationAlerts.length > 1) {
      alertMessage = intl.formatMessage({
        id: 'notifications.numberOfAlerts',
        defaultMessage: 'You have number of alerts. Please check your notice board'
      });
    } else {
      alertMessage = notifications.showNotificationAlerts[0].Message;
      alertType = notifications.showNotificationAlerts[0].AlertType;
    }
    showAlert = true;
    //Remove the alert
    setTimeout(resetNotificationAlert, 8000);
  }

  const closeAlertHandler = () => {
    resetNotificationAlert();
  };

  //Notification time
  useEffect(() => {
    const timer = setTimeout(() => {
      doNotificationPolling();

      if (interval !== statusFetchInterval) {
        setInterval(statusFetchInterval);
      }

      setTime(time + 1);
    }, interval);

    return () => {
      clearTimeout(timer);
    };
  }, [time]);

  const imgClass = !show ? s.showImage : s.hideImage;

  // Dynamic Navigation Logic
  const envNameShort = process.env.REACT_APP_ENV_NAME_SHORT;
  const userId = Cookies.get(`userid-${envNameShort}`);
  const profileId = Cookies.get(`selectedprofileid-${envNameShort}`);
  const organisationId = Cookies.get(`selectedorganisationid-${envNameShort}`);

  const { getLink, addLink, updateLink } = useDynamicLinks(userId, organisationId, profileId);

  const [showPinModal, setShowPinModal] = useState(false);
  const currentPath = location.pathname;
  const pinnedLink = getLink(currentPath);

  // Drag Logic
  const [position, setPosition] = useState({ bottom: 20, right: 80 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = React.useRef({ x: 0, y: 0 });

  const handleMouseDown = (e) => {
    setIsDragging(false);
    dragStartPos.current = { x: e.clientX, y: e.clientY };

    const handleMouseMove = (moveEvent) => {
      const dx = dragStartPos.current.x - moveEvent.clientX; // Invert dx because we use 'right'
      const dy = dragStartPos.current.y - moveEvent.clientY; // Invert dy because we use 'bottom'

      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        setIsDragging(true);
      }

      if (isDragging || Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        setPosition(prev => ({
          bottom: prev.bottom + dy,
          right: prev.right + dx
        }));
        dragStartPos.current = { x: moveEvent.clientX, y: moveEvent.clientY };
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handlePinClick = (e) => {
    if (!isDragging) {
      setShowPinModal(true);
    }
  };

  const handlePinSave = (title) => {
    if (pinnedLink) {
      updateLink(currentPath, title);
    } else {
      addLink({ title, path: currentPath });
    }
  };


  return (
    <div>
      {envName !== PRODUCTION_ENV && envName !== ELEIOT_ENV && envName !== STAGING_ENV && (
        <div className={`${s.envBar} ${envClass}`}>You are in {process.env.REACT_APP_ENV_NAME} environment</div>
      )}

      <div className={s.wrapper}>
        <Sidebar logout={logout} location={location} />
        <div className={classNames(s.contentWrapper, contentWrapperClass)}>
          <nav className={classNames(`${s.hideDesktop} navbar navbar-expand-lg`)}></nav>
          <div className={classNames(sidebarClass, s.overlay)} onClick={() => handleViewSidebar(true)}></div>

          {/* Pin Icon - Movable */}
          <div
            style={{
              position: 'fixed',
              bottom: `${position.bottom}px`,
              right: `${position.right}px`,
              zIndex: 999
            }}
            onMouseDown={handleMouseDown}
          >
            <div
              title={pinnedLink ? "Edit Pin" : "Pin Page"}
              onClick={handlePinClick}
              style={{
                backgroundColor: pinnedLink ? '#00A1E4' : '#ffffff',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                cursor: isDragging ? 'grabbing' : 'pointer',
                border: '1px solid #e0e0e0',
                userSelect: 'none'
              }}
            >
              <div style={{
                transform: pinnedLink ? 'rotate(0deg)' : 'rotate(45deg)',
                transition: 'transform 0.3s ease',
                display: 'flex'
              }}>
                <PinIcon fill={pinnedLink ? '#ffffff' : '#757575'} width="20" height="20" />
              </div>
            </div>
          </div>

          {children}

          <div className="container-fluid">
            <img
              src={expandBlue}
              alt="Collapse"
              className={classNames(s.sidebarCollapse, s.hideDesktop)}
              onClick={() => handleViewSidebar(false)}
            />
          </div>

          <div className={s.noticeContainer}>
            <div className={s.noticeBoard} style={WidgetVisibility(userProfileWidgets, 'NOTICEBOARD')}>
              <img src={notification} alt="Notification" onClick={handleShow} className={imgClass}></img>
            </div>
            <div className={classNames(s.circle, imgClass)}> {notifications.notificationsHistory.length}</div>
          </div>
        </div>
      </div>

      <NoticeBoard show={show} handleClose={handleClose} />
      <NotificationAlert showBanner={showAlert} status={alertType} message={alertMessage} closeHandler={closeAlertHandler} />
      <PinPageModal
        show={showPinModal}
        onHide={() => setShowPinModal(false)}
        onSave={handlePinSave}
        initialTitle={pinnedLink ? pinnedLink.title : ''}
      />
    </div>
  );
};


Layout.propTypes = {
  isAuthenticated: PropTypes.bool,
  logout: PropTypes.func,
  handleViewSidebar: PropTypes.func,
  currentSidebarOpen: PropTypes.bool,
  userProfileWidgets: PropTypes.object
};

export default injectIntl(Layout);